import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";

export default class HotelPayment extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      payments: [],
      folios: [],
      paymentMethods: [],
      currencies: [],
      loading: false,
      error: null,

      showModal: false,
      editItem: null,
      newItem: {
        paymentId: null,
        storeId: "",
        folioId: "",
        reservationId: "",
        paymentMethodId: "",
        amount: "",
        currencyId: "",
        transactionRef: "",
        paidAt: "",
        notes: "",
      },
    };
  }

  apiUrl = (resource) => {
    const baseRaw = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
    const base = baseRaw || `${window.location.origin.replace(/\/+$/, "")}/api`;
    const path = resource.replace(/^\/+/, "");
    const storeId = this.context.storeId || "";
    const sep = path.includes("?") ? "&" : "?";
    return storeId
      ? `${base}/${path}${sep}storeId=${encodeURIComponent(storeId)}`
      : `${base}/${path}`;
  };

  fetchWithAuth = async (url, opts = {}) => {
    const headers = { ...(opts.headers || {}) };
    if (
      opts.body &&
      !(opts.body instanceof FormData) &&
      !headers["Content-Type"]
    ) {
      headers["Content-Type"] = "application/json";
    }
    const token = this.props.authToken || "";
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  };

  componentDidMount() {
    this.loadAllData();
  }

  loadAllData = async () => {
    this.setState({ loading: true, error: null });
    try {
      const [payRes, folioRes, methodRes, currencyRes] = await Promise.all([
        this.fetchWithAuth(this.apiUrl("HotelPayment")),
        this.fetchWithAuth(this.apiUrl("HotelFolio")),
        this.fetchWithAuth(this.apiUrl("PaymentMethod")),
        this.fetchWithAuth(this.apiUrl("Currency")),
      ]);

      if (!payRes.ok)
        throw new Error(await payRes.text().catch(() => payRes.statusText));
      if (!folioRes.ok)
        throw new Error(await folioRes.text().catch(() => folioRes.statusText));
      if (!methodRes.ok)
        throw new Error(
          await methodRes.text().catch(() => methodRes.statusText)
        );
      if (!currencyRes.ok)
        throw new Error(
          await currencyRes.text().catch(() => currencyRes.statusText)
        );

      const [payments, folios, paymentMethods, currencies] = await Promise.all([
        payRes.json(),
        folioRes.json(),
        methodRes.json(),
        currencyRes.json(),
      ]);

      // normalize empty numeric/nullable fields for form usage
      const normalizedPayments = (payments || []).map((p) => ({
        ...p,
        amount: p.amount != null ? p.amount : "",
      }));

      this.setState({
        payments: normalizedPayments,
        folios: folios || [],
        paymentMethods: paymentMethods || [],
        currencies: currencies || [],
      });
    } catch (err) {
      console.error("Error loading payment data:", err);
      this.setState({ error: err.message || "Failed to load payment data." });
    } finally {
      this.setState({ loading: false });
    }
  };

  // When editing, fetch full payment detail (GET /api/HotelPayment/{id}) so we have folioId/paymentMethodId/currencyId
  handleShowModal = async (item = null) => {
    if (!item) {
      // Add new
      const today = new Date().toISOString().split("T")[0];
      this.setState({
        showModal: true,
        editItem: null,
        newItem: {
          paymentId: null,
          storeId: this.context.storeId || "",
          folioId: "",
          reservationId: "",
          paymentMethodId: "",
          amount: "",
          currencyId: "",
          transactionRef: "",
          paidAt: today,
          notes: "",
        },
        error: null,
      });
      return;
    }

    // Edit: fetch full payment detail
    this.setState({ loading: true, error: null });
    try {
      const res = await this.fetchWithAuth(
        this.apiUrl(`HotelPayment/${item.paymentId}`)
      );
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || `HTTP ${res.status}`);
      }
      const detail = await res.json();

      // map server fields to form fields; PaidAt arrives as ISO string or null
      const paidAtDate = detail.paidAt ? detail.paidAt.split("T")[0] : "";

      this.setState({
        showModal: true,
        editItem: detail, // used to decide PUT vs POST
        newItem: {
          paymentId: detail.paymentId || null,
          storeId: detail.storeId || this.context.storeId || "",
          folioId: detail.folioId || "",
          reservationId: detail.reservationId || "",
          paymentMethodId: detail.paymentMethodId || "",
          amount: detail.amount != null ? detail.amount : "",
          currencyId: detail.currencyId || "",
          transactionRef: detail.transactionRef || "",
          paidAt: paidAtDate,
          notes: detail.notes || "",
        },
        error: null,
      });
    } catch (err) {
      console.error("Error loading payment detail:", err);
      this.setState({ error: err.message || "Failed to load payment detail." });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleCloseModal = () =>
    this.setState({ showModal: false, editItem: null, error: null });

  handleChange = (e) => {
    const { name, value } = e.target;
    // Keep numbers as strings while editing to avoid controlled/uncontrolled issues
    this.setState((prev) => ({ newItem: { ...prev.newItem, [name]: value } }));
  };

  handleSave = async () => {
    const { newItem, editItem } = this.state;
    const method = editItem ? "PUT" : "POST";
    const url = editItem
      ? this.apiUrl(`HotelPayment/${editItem.paymentId}`)
      : this.apiUrl("HotelPayment");

    // build payload matching HotelPayment model on server
    const payload = {
      storeId: this.context.storeId || newItem.storeId,
      folioId: newItem.folioId || null,
      reservationId: newItem.reservationId || null,
      paymentMethodId: newItem.paymentMethodId || null,
      amount: newItem.amount === "" ? 0 : parseFloat(newItem.amount),
      currencyId: newItem.currencyId || null,
      transactionRef: newItem.transactionRef || null,
      paidAt: newItem.paidAt || null,
      notes: newItem.notes || null,
    };

    this.setState({ loading: true, error: null });
    try {
      const res = await this.fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || `HTTP ${res.status}`);
      }

      this.handleCloseModal();
      await this.loadAllData();
    } catch (err) {
      console.error("Error saving payment:", err);
      this.setState({ error: err.message || "Error saving payment." });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment?"))
      return;
    this.setState({ loading: true, error: null });
    try {
      const res = await this.fetchWithAuth(this.apiUrl(`HotelPayment/${id}`), {
        method: "DELETE",
      });
      if (!res.ok)
        throw new Error(await res.text().catch(() => res.statusText));
      await this.loadAllData();
    } catch (err) {
      console.error("Error deleting payment:", err);
      this.setState({ error: err.message || "Failed to delete payment." });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      payments,
      folios,
      paymentMethods,
      currencies,
      showModal,
      newItem,
      editItem,
      loading,
      error,
    } = this.state;

    return (
      <div className="container mt-4">
        <h4>Hotel Payments</h4>

        <Row className="mb-2 align-items-center">
          <Col>
            <div>
              <Button
                variant="primary"
                onClick={() => this.handleShowModal()}
                disabled={loading}
              >
                Add Payment
              </Button>
            </div>
          </Col>
          <Col className="text-end">
            {loading && <Spinner animation="border" size="sm" />}
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        <Table bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th>Folio No</th>
              <th>Method</th>
              <th>Amount</th>
              <th>Currency</th>
              <th>Transaction Ref</th>
              <th>Paid On</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(!payments || payments.length === 0) && (
              <tr>
                <td colSpan="8" className="text-center">
                  No payments
                </td>
              </tr>
            )}
            {payments.map((p) => (
              <tr key={p.paymentId}>
                <td>{p.folioNo || "—"}</td>
                <td>{p.method || "—"}</td>
                <td>
                  {p.amount !== "" && p.amount != null
                    ? parseFloat(p.amount).toFixed(2)
                    : "—"}
                </td>
                <td>{p.currency || "—"}</td>
                <td>{p.transactionRef || "—"}</td>
                <td>{p.paidAt ? p.paidAt.split("T")[0] : "—"}</td>
                <td>{p.notes || "—"}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => this.handleShowModal(p)}
                  >
                    Edit
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => this.handleDelete(p.paymentId)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Modal for Add/Edit */}
        <Modal show={showModal} onHide={this.handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {editItem ? "Edit Payment" : "Add Payment"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.error && (
              <Alert variant="danger">{this.state.error}</Alert>
            )}

            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Folio</Form.Label>
                    <Form.Select
                      name="folioId"
                      value={newItem.folioId || ""}
                      onChange={this.handleChange}
                    >
                      <option value="">-- Select --</option>
                      {folios.map((f) => (
                        <option key={f.folioId} value={f.folioId}>
                          {f.folioNo}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Method</Form.Label>
                    <Form.Select
                      name="paymentMethodId"
                      value={newItem.paymentMethodId || ""}
                      onChange={this.handleChange}
                    >
                      <option value="">-- Select --</option>
                      {paymentMethods.map((m) => (
                        <option
                          key={m.paymentMethodId}
                          value={m.paymentMethodId}
                        >
                          {m.code || m.description || m.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="amount"
                      value={newItem.amount || ""}
                      onChange={this.handleChange}
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency</Form.Label>
                    <Form.Select
                      name="currencyId"
                      value={newItem.currencyId || ""}
                      onChange={this.handleChange}
                    >
                      <option value="">-- Select --</option>
                      {currencies.map((c) => (
                        <option key={c.currencyId} value={c.currencyId}>
                          {c.isocode || c.name || c.symbol}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Transaction Reference</Form.Label>
                <Form.Control
                  name="transactionRef"
                  value={newItem.transactionRef || ""}
                  onChange={this.handleChange}
                  placeholder="Bank Ref / UPI / Gateway ID"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Paid At</Form.Label>
                <Form.Control
                  type="date"
                  name="paidAt"
                  value={newItem.paidAt || ""}
                  onChange={this.handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={newItem.notes || ""}
                  onChange={this.handleChange}
                  placeholder="Optional remarks"
                />
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={this.handleSave}
              disabled={loading}
            >
              {editItem ? "Update" : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
